/**
 * src/components/flo/floModel.js
 *
 * 공급 흐름도 — 의미 그래프를 배치까지 끝난 뷰 모델로 접는 순수 함수.
 *
 * 입력(공개 계약, 호출부가 만든다):
 *   { dir: "up"|"down",
 *     lanes: [{ key, name, code?, type? }],
 *     nodes: [{ key, kind: "inv"|"make"|"kit"|"move", lane, name, sub?, code?, rank?,
 *               root?, purchased?, more?, supply?: "none"|"unknown", warnings?: string[](재고만),
 *               tip?: string[], resource?: { name, count, tip? } }],
 *     edges: [{ from, to, kind: "make"|"kit"|"move", qty?, ratio?, back? }] }
 *
 * 입력 규칙(검증하지 않는 것 포함):
 *   엣지 from->to 는 dir 과 무관하게 물리 흐름(공급 -> 수요) 방향이다.
 *   정방향 엣지는 재고와 공정을 번갈아 잇는다 — 재고->재고는 back 일 때만 쓴다.
 *   ratio 는 퍼센트(0~100) 수다. qty 가 함께 있으면 칩은 qty 만 보인다.
 *   key 는 문자열로 주고 노드 key 는 유일해야 한다(엄격 비교).
 *   이름·부제·툴팁·경고는 호출자가 번역해서 넣는다 — 범례·레인 유형 문구는 컴포넌트가 번역한다.
 *   필드별 뜻과 예제: saas-web/.claude/skills/flo/SKILL.md
 *
 * 출력(배치까지 끝난 뷰 모델 — floGeometry.computeGeometry/buildEdges 가 그대로 소비한다):
 *   { dir,
 *     lanes: [{k, label, code, type, h}],
 *     nodes: [{k, key, t, lane, c, y, label, code?, sub?, root?, pur?, x?, supply?,
 *              g?, tip?, res?, n?, items?}],
 *     edges: [{s, t, kind, q?, r?, w?, po?, pi?, back?, ch?}],
 *     legend: string[],
 *     warnings: string[] }
 *
 * 파이프라인은 검증 -> 내부 id 부여 -> 잎 재고 접기(fold) -> 배치(floLayout.layoutGraph)
 * -> 출력 id 재부여 -> 노드·엣지·범례·경고 조립 순서다.
 * 잎 접기는 그려질 그래프 자체를 보고 판단한다(foldLeaves) — 호출자는 담을 양만 정하고 접지 않는다.
 *
 * 결정형이다 — nodes·edges·lanes 배열의 입력 순서를 섞어도 같은 JSON 이 나온다.
 * Map 으로 상태를 쥐고, 정렬은 전부 명시적 비교자를 쓴다(localeCompare 는 쓰지 않는다).
 *
 * 중립 leaf — pages/** 를 import 하지 않는다.
 */
import numberUtil from "@plannel/utils/number-util";
import { layoutGraph } from "@plannel/components/flo/floLayout";
import {
  INV_H_U, INV_ANCHOR_U, PROC_H_U, PROC_ANCHOR_U, RES_H_U, RES_ANCHOR_U,
} from "@plannel/components/flo/floGeometry";

// 레인 정렬 — 물리 흐름의 상류가 위에 온다. 모르는 유형은 맨 뒤(9)다.
export const DEFAULT_LANE_TYPE_ORDER = ["VENDOR", "PLANT", "WHSE", "DC", "VIRTUAL_DC", "STO", "CUST"];

// 노드 종류 — inv(재고) 는 따로, 나머지 셋은 공정이다.
const NODE_KINDS = new Set(["inv", "make", "kit", "move"]);
const EDGE_KINDS = new Set(["make", "kit", "move"]);
// 잎이 이보다 많이 모이면 나머지를 접는다. 접힌 노드 툴팁에 싣는 이름 수 상한.
const AGG_NAMES = 30;

/** 문자열 비교 — localeCompare 를 쓰지 않고 코드 유닛 순서로만 가른다. */
function cmpStr(a, b) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** 정렬 키용 8자리 0채움 — rank 가 없으면 0 으로 본다. */
function pad8(n) {
  return String(Math.max(0, Math.trunc(Number(n) || 0))).padStart(8, "0");
}

/** 노드의 내부 id — 재고는 i:, 공정(make·kit·move)은 p: 접두다. */
function internalId(node) {
  return (node.kind === "inv" ? "i:" : "p:") + node.key;
}

/**
 * 입력 그래프를 검증한다.
 * 종류를 모르거나 레인 목록에 없는 레인을 가리키는 노드, 끝점이 없거나 종류가 이상한 엣지는 버린다.
 * 같은 key 의 노드가 여럿이면 모두 버린다 — key 는 호출자가 유일하게 주는 값이라 어느 쪽도 믿을 수 없다.
 * 같은 (from,to) 가 여럿이면 결정형으로 정렬한 뒤 첫 번째만 남긴다.
 * 무엇이든 버렸으면 한 번만 경고를 남긴다.
 */
function validate(graph) {
  const rawNodes = graph?.nodes || [];
  const laneKeys = new Set((graph?.lanes || []).map((l) => l && l.key));
  const nodeByKey = new Map();
  const dupKeys = new Set();
  let droppedNodes = 0;
  for (const n of rawNodes) {
    if (!n || !NODE_KINDS.has(n.kind) || !laneKeys.has(n.lane)) { droppedNodes += 1; continue; }
    if (nodeByKey.has(n.key)) dupKeys.add(n.key);
    else nodeByKey.set(n.key, n);
  }
  for (const k of dupKeys) {
    nodeByKey.delete(k);
    droppedNodes += rawNodes.filter((n) => n && n.key === k).length;
  }

  const rawEdges = graph?.edges || [];
  const survivors = [];
  let droppedEdges = 0;
  for (const e of rawEdges) {
    if (!e || !nodeByKey.has(e.from) || !nodeByKey.has(e.to) || !EDGE_KINDS.has(e.kind)) {
      droppedEdges += 1;
      continue;
    }
    survivors.push(e);
  }
  const sortKey = (e) => (
    [e.from, e.to, e.kind, String(e.qty ?? ""), String(e.ratio ?? ""), e.back ? "1" : "0"].join("\u0001")
  );
  survivors.sort((a, b) => cmpStr(sortKey(a), sortKey(b)));
  const seenPair = new Set();
  const edges = [];
  for (const e of survivors) {
    const pk = `${e.from}\u0001${e.to}`;
    if (seenPair.has(pk)) { droppedEdges += 1; continue; }
    seenPair.add(pk);
    edges.push(e);
  }

  if (droppedNodes > 0 || droppedEdges > 0) {
    // eslint-disable-next-line no-console
    console.warn(`floModel: 잘못된 노드 ${droppedNodes}개, 잘못됐거나 중복된 엣지 ${droppedEdges}개를 건너뛰었다.`);
  }
  return { nodeByKey, edges };
}

/** 항목 하나(재고·공정·접힘)의 배치 정렬 키. 접힘은 늘 재고·공정 뒤에 온다. */
function tieOf(items, id) {
  const it = items.get(id);
  if (!it) return id;
  if (it.t === "agg") return `2|${id}`;
  if (it.t === "inv") return `0|${pad8(it.rank)}|${it.key}`;
  return `1|${pad8(it.rank)}|${it.key}`;
}

/** 항목의 U 단위 크기 [높이, 앵커] — 설비 딸린 생산만 커진다. */
function sizeOf(it) {
  if (it.t === "inv" || it.t === "agg") return [INV_H_U, INV_ANCHOR_U];
  if (it.t === "make" && it.node && it.node.resource) return [RES_H_U, RES_ANCHOR_U];
  return [PROC_H_U, PROC_ANCHOR_U];
}

/**
 * 같은 대상에 같은 방식으로 모이는 잎 재고가 많으면 나머지를 접힘 노드 하나로 접는다.
 *
 * 잎 후보는 뿌리도 아니고 더 있음(more)도 아니고 되돌이 호에 닿지도 않은 재고이며,
 * up 은 들어오는 호가 없고 down 은 나가는 호가 없다.
 *   up 생산·키팅  잎이 먹이는 공정이 전부 한 종류다 — 그 공정들이 닻이고, 잎만 접는다(무게 1).
 *   up 운송      잎이 운송 하나에만 먹이고, 그 운송은 입출 각 하나다 — 도착 재고가 닻,
 *                잎+운송을 같이 접는다(무게 2).
 *   down         잎에 들어오는 공정이 하나이고, 그 공정도 입출 각 하나다 — 투입 재고가 닻,
 *                공정+잎을 같이 접는다(무게 2).
 * 그룹은 (닻, 종류, 잎의 레인) 으로 묶고, 정렬된 그룹 순서대로 접는다.
 * foldLimit 미만 2 면 아예 접지 않는다.
 */
function foldLeaves(dir, items, bipartiteArcs, backArcs, foldLimit) {
  if (foldLimit < 2) return { dropSet: new Set(), foldArcs: [] };

  const outs = new Map();
  const ins = new Map();
  for (const a of bipartiteArcs) {
    if (!outs.has(a.s)) outs.set(a.s, []);
    outs.get(a.s).push(a.t);
    if (!ins.has(a.t)) ins.set(a.t, []);
    ins.get(a.t).push(a.s);
  }
  const touchedByBack = new Set();
  for (const a of backArcs) { touchedByBack.add(a.s); touchedByBack.add(a.t); }

  const groups = new Map();
  const addMember = (anchor, kind, lane, leafId, unit) => {
    const anchorSorted = anchor.slice().sort(cmpStr);
    const gk = `${anchorSorted.join(",")}|${kind}|${lane}`;
    if (!groups.has(gk)) groups.set(gk, { anchor: anchorSorted, kind, lane, members: [] });
    groups.get(gk).members.push({ id: leafId, unit });
  };

  for (const [id, it] of items) {
    if (it.t !== "inv") continue;
    if (it.node.root || it.node.more) continue;
    if (touchedByBack.has(id)) continue;
    if (dir === "up") {
      if ((ins.get(id) || []).length > 0) continue;
      const procsOf = (outs.get(id) || []).slice().sort(cmpStr);
      if (!procsOf.length) continue;
      const kinds = new Set(procsOf.map((p) => items.get(p)?.t));
      if (kinds.size === 1 && kinds.has("move")) {
        if (procsOf.length !== 1) continue;
        const proc = procsOf[0];
        if ((ins.get(proc) || []).length !== 1 || (outs.get(proc) || []).length !== 1) continue;
        addMember([outs.get(proc)[0]], "move", it.lane, id, [id, proc]);
      } else if (kinds.has("move") || kinds.size !== 1) {
        continue;
      } else {
        addMember(procsOf, [...kinds][0], it.lane, id, [id]);
      }
    } else {
      if ((outs.get(id) || []).length > 0) continue;
      const insOf = ins.get(id) || [];
      if (insOf.length !== 1) continue;
      const proc = insOf[0];
      const procIt = items.get(proc);
      if (!procIt) continue;
      if ((ins.get(proc) || []).length !== 1 || (outs.get(proc) || []).length !== 1) continue;
      addMember([ins.get(proc)[0]], procIt.t, it.lane, id, [proc, id]);
    }
  }

  const dropSet = new Set();
  const foldArcs = [];
  let foldCounter = 0;
  const groupKeys = [...groups.keys()].sort(cmpStr);
  for (const gk of groupKeys) {
    const g = groups.get(gk);
    if (g.members.length <= foldLimit) continue;
    const sortedMembers = g.members.slice().sort((a, b) => cmpStr(tieOf(items, a.id), tieOf(items, b.id)));
    const folded = sortedMembers.slice(foldLimit - 1);
    const names = folded.slice(0, AGG_NAMES).map((m) => {
      const leaf = items.get(m.id);
      return leaf?.node?.name || leaf?.node?.code || "";
    });
    const foldId = `f:${foldCounter}`;
    foldCounter += 1;
    for (const m of folded) for (const u of m.unit) dropSet.add(u);
    items.set(foldId, {
      t: "agg", key: foldId, lane: g.lane, rank: 0, node: null, n: folded.length, names,
    });
    const span = (dir === "up" && g.kind !== "move") ? 1 : 2;
    for (const anchorId of g.anchor) {
      foldArcs.push(dir === "up"
        ? { s: foldId, t: anchorId, kind: g.kind, w: span, back: false }
        : { s: anchorId, t: foldId, kind: g.kind, w: span, back: false });
    }
  }
  return { dropSet, foldArcs };
}

/** 레인 정렬 키 — [유형 순위(모르면 9), 레인 키]. floLayout.layoutGraph 의 laneRank 그대로다. */
function buildLaneRank(laneKeysUsed, laneByKey, laneTypeOrder) {
  const orderIndex = new Map(laneTypeOrder.map((t, i) => [t, i]));
  const rank = {};
  for (const lk of laneKeysUsed) {
    const def = laneByKey.get(lk);
    const idx = def && orderIndex.has(def.type) ? orderIndex.get(def.type) : 9;
    rank[lk] = [idx, lk];
  }
  return rank;
}

/**
 * 의미 그래프 -> 배치까지 끝난 뷰 모델.
 *
 * @param {object} graph 위 헤더의 입력 계약
 * @param {object} [opts]
 * @param {number} [opts.foldLimit] 그룹당 잎이 이보다 많으면 나머지를 접는다(기본 6, 2 미만이면 안 접는다)
 * @param {string[]} [opts.laneTypeOrder] 레인 유형 정렬 순서(기본 DEFAULT_LANE_TYPE_ORDER)
 */
export function buildFloModel(graph, opts = {}) {
  const { foldLimit = 6, laneTypeOrder = DEFAULT_LANE_TYPE_ORDER } = opts;
  const dir = graph?.dir === "down" ? "down" : "up";
  const laneByKey = new Map((graph?.lanes || []).map((l) => [l.key, l]));

  const { nodeByKey, edges } = validate(graph);

  const items = new Map();
  for (const n of nodeByKey.values()) {
    items.set(internalId(n), {
      t: n.kind, key: n.key, lane: n.lane, rank: n.rank || 0, node: n,
    });
  }

  const bipartiteArcs = [];
  const backArcsList = [];
  for (const e of edges) {
    const sNode = nodeByKey.get(e.from);
    const tNode = nodeByKey.get(e.to);
    const rec = {
      s: internalId(sNode), t: internalId(tNode), kind: e.kind, qty: e.qty, ratio: e.ratio, back: !!e.back,
    };
    (rec.back ? backArcsList : bipartiteArcs).push(rec);
  }

  const { dropSet, foldArcs } = foldLeaves(dir, items, bipartiteArcs, backArcsList, foldLimit);
  for (const id of dropSet) items.delete(id);

  const arcsAll = [
    ...bipartiteArcs.filter((a) => !dropSet.has(a.s) && !dropSet.has(a.t)),
    ...backArcsList.filter((a) => !dropSet.has(a.s) && !dropSet.has(a.t)),
    ...foldArcs,
  ];

  const laneKeysUsed = new Set([...items.values()].map((it) => it.lane));
  const laneRank = buildLaneRank(laneKeysUsed, laneByKey, laneTypeOrder);

  const lnodes = [...items.entries()].map(([id, it]) => {
    const [hU, aU] = sizeOf(it);
    return { key: id, lane: it.lane, h: hU, anchor: aU, tie: tieOf(items, id) };
  });
  const roots = [...items.entries()]
    .filter(([, it]) => it.t === "inv" && it.node && it.node.root)
    .map(([id]) => id);

  const arcRecords = arcsAll.slice().sort((a, b) => (
    cmpStr(tieOf(items, a.s), tieOf(items, b.s))
    || cmpStr(tieOf(items, a.t), tieOf(items, b.t))
    || cmpStr(a.s, b.s) || cmpStr(a.t, b.t)
  ));
  const forcedBack = [];
  arcRecords.forEach((a, i) => { if (a.back) forcedBack.push(i); });

  const lay = layoutGraph({
    nodes: lnodes,
    arcs: arcRecords.map((a) => ({ s: a.s, t: a.t, w: a.w })),
    roots,
    walkUp: dir === "up",
    laneRank,
    back: forcedBack,
  });

  // 출력 id — (층, 레인 순번, y, 내부 id) 순으로 늘어놓고 종류별 접두(n·p·a)를 붙인다.
  const laneIndexOf = new Map(lay.lanes.map((lk, i) => [lk, i]));
  const orderedIds = [...items.keys()].sort((a, b) => {
    const la = lay.layer[a];
    const lb = lay.layer[b];
    if (la !== lb) return la - lb;
    const lia = laneIndexOf.get(items.get(a).lane);
    const lib = laneIndexOf.get(items.get(b).lane);
    if (lia !== lib) return lia - lib;
    const ya = lay.y[a];
    const yb = lay.y[b];
    if (ya !== yb) return ya - yb;
    return cmpStr(a, b);
  });
  const idMap = new Map();
  const counters = { n: 0, p: 0, a: 0 };
  for (const id of orderedIds) {
    const it = items.get(id);
    const prefix = it.t === "inv" ? "n" : (it.t === "agg" ? "a" : "p");
    idMap.set(id, `${prefix}${counters[prefix]}`);
    counters[prefix] += 1;
  }

  const laneOutId = new Map(lay.lanes.map((lk, i) => [lk, `l${i}`]));
  const lanesOut = lay.lanes.map((lk, i) => {
    const def = laneByKey.get(lk) || {};
    return {
      k: `l${i}`, label: def.name || lk, code: def.code || lk, type: def.type || "", h: lay.laneH[lk],
    };
  });

  const outNodes = [];
  const warnSeen = new Set();
  const warnings = [];
  const flags = {
    stock: false, purchased: false, root: false, make: false, kit: false, transport: false,
    resource: false, resourceMore: false, more: false, folded: false,
  };
  for (const id of orderedIds) {
    const it = items.get(id);
    const rec = {
      k: idMap.get(id), key: it.key, t: it.t, lane: laneOutId.get(it.lane), c: lay.layer[id], y: lay.y[id],
    };
    if (it.t === "agg") {
      rec.label = "";
      rec.n = it.n;
      rec.items = it.names;
      flags.folded = true;
      outNodes.push(rec);
      continue;
    }
    const n = it.node;
    rec.label = n.name;
    if (n.code) rec.code = n.code;
    if (n.sub) rec.sub = n.sub;
    if (n.root) { rec.root = true; flags.root = true; }
    if (n.purchased) { rec.pur = true; flags.purchased = true; }
    if (n.more) { rec.x = true; flags.more = true; }
    if (n.supply) rec.supply = n.supply;
    if (it.t === "inv" && n.warnings && n.warnings.length) {
      rec.g = n.warnings;
      for (const w of n.warnings) {
        if (!warnSeen.has(w)) { warnSeen.add(w); warnings.push(w); }
      }
    }
    if (n.tip) rec.tip = n.tip;
    if (it.t === "inv") {
      flags.stock = true;
      const laneDef = laneByKey.get(it.lane);
      if (laneDef && laneDef.type === "VENDOR") flags.purchased = true;
    }
    // 생산과 키팅은 색·아이콘이 달라 범례도 따로 둔다 — 묶으면 한쪽 색이 설명되지 않는다.
    if (it.t === "make") flags.make = true;
    if (it.t === "kit") flags.kit = true;
    if (it.t === "move") flags.transport = true;
    if (n.resource) {
      rec.res = { label: n.resource.name, n: n.resource.count, tip: n.resource.tip || [] };
      flags.resource = true;
      if (n.resource.count > 1) flags.resourceMore = true;
    }
    outNodes.push(rec);
  }

  const outEdges = [];
  let hasMaterialFlow = false;
  let hasTransferFlow = false;
  let hasQty = false;
  let hasRatio = false;
  arcRecords.forEach((a, i) => {
    const e = { s: idMap.get(a.s), t: idMap.get(a.t), kind: a.kind };
    if (a.kind === "make" || a.kind === "kit") hasMaterialFlow = true;
    if (a.kind === "move") hasTransferFlow = true;
    if (a.qty != null) { e.q = `×${numberUtil.formatNumber(a.qty)}`; hasQty = true; }
    if (a.ratio != null) { e.r = `${numberUtil.formatNumber(a.ratio, { maximumFractionDigits: 0 })}%`; hasRatio = true; }
    if (lay.waypoints[i]) e.w = lay.waypoints[i];
    if ((lay.nOut[a.s] || 0) >= 2) e.po = lay.portOut[i];
    if ((lay.nIn[a.t] || 0) >= 2) e.pi = lay.portIn[i];
    if (a.back) { e.back = 1; e.ch = lay.channels[i]; }
    outEdges.push(e);
  });

  const legendOrder = [
    ["stock", flags.stock], ["purchased", flags.purchased], ["root", flags.root],
    ["make", flags.make], ["kit", flags.kit], ["transport", flags.transport], ["resource", flags.resource],
    ["resourceMore", flags.resourceMore], ["materialFlow", hasMaterialFlow],
    ["transferFlow", hasTransferFlow], ["qty", hasQty], ["ratio", hasRatio],
    ["more", flags.more], ["folded", flags.folded],
  ];
  const legend = legendOrder.filter(([, used]) => used).map(([key]) => key);

  return {
    dir, lanes: lanesOut, nodes: outNodes, edges: outEdges, legend, warnings,
  };
}
